import { useEffect } from 'react';
import { site } from '../../content/site';
import { seoDefaults, type PageSeo } from '../../content/seo';
import { absoluteUrl } from '../../lib/config';
import { fillTemplate } from '../../lib/format';

/**
 * Marks the tags this component owns, so it can rewrite them on navigation
 * instead of appending a second set.
 */
const MANAGED = 'data-seo-managed';

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  const selector = `meta[${attribute}="${key}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    tag.setAttribute(MANAGED, '');
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  const selector = `link[rel="${rel}"]`;
  let tag = document.head.querySelector<HTMLLinkElement>(selector);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    tag.setAttribute(MANAGED, '');
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

/**
 * Per-page title, description, canonical, and social metadata (spec section 18).
 *
 * Written imperatively rather than through React's document metadata hoisting
 * because the behaviour is then identical in the browser, in jsdom, and under
 * test, and because every tag is rewritten on navigation rather than stacked.
 *
 * The whole site is client-rendered, so a crawler that does not execute
 * JavaScript sees the fallback metadata in index.html. That is a known limit of
 * this phase; if indexing of `/owners` and `/providers` matters more later, the
 * fix is prerendering those routes at build time, not a different tag strategy.
 */
export function Seo({ seo }: { seo: PageSeo }) {
  useEffect(() => {
    const title = fillTemplate(seoDefaults.titleTemplate, {
      page: seo.title,
      brand: site.brandName,
    });
    const canonical = absoluteUrl(seo.path);
    const imageUrl = absoluteUrl(seoDefaults.ogImagePath);

    document.title = title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'robots', seo.noindex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:type', seoDefaults.ogType);
    upsertMeta('property', 'og:site_name', site.brandName);
    upsertMeta('property', 'og:locale', seoDefaults.locale);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', imageUrl);
    upsertMeta('property', 'og:image:alt', seoDefaults.ogImageAlt);

    upsertMeta('name', 'twitter:card', seoDefaults.twitterCard);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', imageUrl);
  }, [seo]);

  return null;
}
